import { Button, TextInput } from "@mantine/core";
import {
  IconLayoutGridFilled,
  IconList,
  IconPlus,
  IconSearch,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import PurchaseOrderCard from "@/components/PurchaseOrderCard";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table/interface";
import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function PurchaseOrderCompletedManagement() {
  const { t } = useTranslation();
  // list or grid
  const [layout, setLayout] = useState("list");
  const [purchaseOrderData, setPurchaseOrderData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const navigate = useNavigate();

  const handleAddPurchaseOrder = () => {
    navigate("/hrMainPage/addPurchaseOrder");
  };

  const handleLayoutChange = (layout) => {
    setLayout(layout);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    const filtered = purchaseOrderData.filter(
      (item) =>
        item?.name?.toLowerCase().includes(value.trim().toLowerCase()) ||
        item?.partner?.name?.toLowerCase().includes(value.trim().toLowerCase())
    );

    if (value === "") {
      setFilteredData(purchaseOrderData);
    } else {
      setFilteredData(filtered);
    }
  };

  const handleDisplayOrderStatus = (orderStatus) => {
    if (orderStatus === "draft") {
      return t("purchaseOrder.draft") || "草稿";
    } else if (orderStatus === "underReview") {
      return t("purchaseOrder.underReview") || "審核中";
    } else if (orderStatus === "approved") {
      return t("purchaseOrder.approved") || "已審核";
    } else if (orderStatus === "completed") {
      return t("purchaseOrder.completed") || "已完成";
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [purchaseOrderResult, partnerResult] = await Promise.all([
          orpcClient.purchaseOrder.list({ filter: { status: "completed" } }),
          orpcClient.partner.list({}),
        ]);

        const completedOrders = purchaseOrderResult.filter(
          (order) => order.status === "completed"
        );

        const ordersWithPartner = completedOrders.map((order) => {
          const partner = partnerResult.find((p) => p.id === order.partnerId);
          return {
            ...order,
            partner: partner || null,
          };
        });

        setPurchaseOrderData(ordersWithPartner);
        setFilteredData(ordersWithPartner);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  const columns: ColumnsType<any> = [
    {
      title: t("purchaseOrder.purchaseOrderName") || "採購單名稱",
      dataIndex: "name",
      key: "name",
      fixed: "left" as const,
      sorter: (a: any, b: any) => (a?.name || "").localeCompare(b?.name || ""),
      render: (text: string) => text || "",
    },
    {
      title: t("purchaseOrder.partner") || "合作方",
      dataIndex: "partner",
      key: "partner",
      sorter: (a: any, b: any) =>
        (a?.partner?.name || "").localeCompare(b?.partner?.name || ""),
      render: (partner: any) => partner?.name || "",
    },
    {
      title: t("purchaseOrder.purchaseDate") || "採購日期",
      dataIndex: "purchaseDate",
      key: "purchaseDate",
      sorter: (a: any, b: any) =>
        dayjs(a?.purchaseDate || 0).valueOf() -
        dayjs(b?.purchaseDate || 0).valueOf(),
      render: (date: any) => (date ? dayjs(date).format("YYYY-MM-DD") : ""),
    },
    {
      title: t("purchaseOrder.deliveryDate") || "交貨日期",
      dataIndex: "deliveryDate",
      key: "deliveryDate",
      sorter: (a: any, b: any) =>
        dayjs(a?.deliveryDate || 0).valueOf() -
        dayjs(b?.deliveryDate || 0).valueOf(),
      render: (date: any) => (date ? dayjs(date).format("YYYY-MM-DD") : ""),
    },
    {
      title: t("purchaseOrder.reviewStatus") || "審核狀態",
      dataIndex: "status",
      key: "status",
      render: (status: any) => handleDisplayOrderStatus(status),
    },
  ];

  const toggleButton = (
    value: string,
    Icon: React.ComponentType<{ size?: number | string }>
  ) => (
    <button
      type="button"
      onClick={() => handleLayoutChange(value)}
      className={`flex h-9 w-9 items-center justify-center rounded-lg border ${
        layout === value
          ? "border-blue-200 bg-blue-50 text-blue-600"
          : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  return !purchaseOrderData || purchaseOrderData?.length === 0 ? (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="w-[487px] rounded-xl bg-white px-16 py-20 shadow-sm">
        <div className="text-center" style={{ color: "#6F6F6F" }}>
          {t("purchaseOrder.noPurchaseOrderData") ||
            "您尚未新增採購單資料！你可以試試："}
        </div>
        <div className="flex text-center">
          <Button
            variant="outline"
            color="gray"
            style={{ width: "100%", margin: "1rem" }}
            radius="md"
            onClick={handleAddPurchaseOrder}
          >
            {t("purchaseOrder.manualAdd") || "手動新增"}
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
              {t("purchaseOrder.completedManagement") || "已完成採購單"}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {t("purchaseOrder.list") || "採購單列表"}
            </div>
          </div>
          <Button
            variant="filled"
            color="gray"
            radius="md"
            leftSection={<IconPlus size={16} />}
            onClick={handleAddPurchaseOrder}
          >
            {t("purchaseOrder.createPurchaseOrder") || "建立採購單"}
          </Button>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-4">
            <TextInput
              className="w-full max-w-md"
              leftSection={<IconSearch size={16} />}
              placeholder={t("purchaseOrder.searchPlaceholder")}
              onChange={handleInputChange}
            />
            <div className="flex gap-2">
              {toggleButton("list", IconList)}
              {toggleButton("grid", IconLayoutGridFilled)}
            </div>
          </div>

          {layout === "list" ? (
            <div className="p-4">
              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                onRow={(record: any) => ({
                  onClick: () =>
                    navigate(`/hrMainPage/purchaseOrder/${record.id}`),
                  style: { cursor: "pointer" },
                })}
                scroll={{ x: "max-content" }}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "25", "50", "100"],
                  showQuickJumper: false,
                }}
                bordered
                sticky
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredData?.map((data) => {
                return (
                  <PurchaseOrderCard
                    key={data.id}
                    props={data}
                    onClick={() =>
                      navigate(`/hrMainPage/purchaseOrder/${data.id}`)
                    }
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PurchaseOrderCompletedManagement;
